import React, { useCallback, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Layout as AntLayout, Menu, Dropdown, Avatar, Space, Button, Tooltip, Drawer } from 'antd';
import {
  UserOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  MenuOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { filterMenuByRole, firstNavigableChild, topMenuConfig } from '../config/menuConfig';
import { useAuth } from '../context/AuthContext';
import { getUserDisplayName, logoutSession, getLoginUrl } from '../api/auth';
import { useNarrowScreen } from '../hooks/useNarrowScreen';

const { Header, Content, Sider } = AntLayout;

const SIDER_COLLAPSED_KEY = 'kfc-sider-collapsed';
const SIDER_WIDTH = 240;

function readSiderCollapsed(): boolean {
  try {
    return localStorage.getItem(SIDER_COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

const Layout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const { session } = useAuth();
  const role = session.identity?.traits?.role as string | undefined;
  const visibleMenus = filterMenuByRole(topMenuConfig, role);
  const [siderCollapsed, setSiderCollapsed] = useState(readSiderCollapsed);
  const [navOpen, setNavOpen] = useState(false);
  const narrow = useNarrowScreen();

  const toggleSider = useCallback(() => {
    setSiderCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDER_COLLAPSED_KEY, next ? '1' : '0');
      } catch {
        /* ignore quota / private mode */
      }
      return next;
    });
  }, []);

  const topMenuItems = visibleMenus.map((item) => ({
    key: item.key,
    icon: item.icon,
    label: item.label,
  }));

  const currentTopKey = '/' + (pathname.split('/').filter(Boolean)[0] || 'shipments');
  const currentConfig = visibleMenus.find((c) => c.key === currentTopKey);
  const sideItems: MenuProps['items'] = currentConfig?.children?.map((child) =>
    child.type === 'divider'
      ? { key: child.key, type: 'divider' as const }
      : { key: child.key, label: child.label }
  ) ?? [];
  const hasSideMenu = sideItems.length > 0;

  const handleLogout = async () => {
    const loginUrl = getLoginUrl();
    try {
      await logoutSession(loginUrl);
      window.location.href = loginUrl;
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'logout',
      label: '退出登录',
      icon: <LogoutOutlined />,
      onClick: handleLogout,
    },
  ];

  const go = (path: string) => {
    navigate(path);
    setNavOpen(false);
  };

  return (
    <AntLayout style={{ minHeight: '100vh' }}>
      <Header style={{ display: 'flex', alignItems: 'center', background: '#001529', paddingInline: narrow ? 8 : 16 }}>
        {narrow ? (
          <Button
            type="text"
            aria-label="打开菜单"
            icon={<MenuOutlined />}
            onClick={() => setNavOpen(true)}
            style={{ color: '#fff', flexShrink: 0, minWidth: 44, minHeight: 44 }}
          />
        ) : hasSideMenu ? (
          <Tooltip title={siderCollapsed ? '展开左侧菜单' : '隐藏左侧菜单'}>
            <Button
              type="text"
              aria-label={siderCollapsed ? '展开左侧菜单' : '隐藏左侧菜单'}
              icon={siderCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={toggleSider}
              style={{ color: '#fff', marginRight: 8, flexShrink: 0 }}
            />
          </Tooltip>
        ) : null}
        <div style={{ color: '#fff', marginRight: narrow ? 0 : 24, fontWeight: 600, flex: narrow ? 1 : undefined, flexShrink: 0 }}>
          {narrow ? 'KFC' : 'KFC 凯飞越履行中心'}
        </div>
        {!narrow && (
          <Menu
            theme="dark"
            mode="horizontal"
            selectedKeys={[currentTopKey]}
            items={topMenuItems}
            onClick={({ key }) => {
              const config = visibleMenus.find((c) => c.key === key);
              navigate(firstNavigableChild(config) || key);
            }}
            style={{ flex: 1, minWidth: 0 }}
          />
        )}
        <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
          <Space style={{ color: '#fff', cursor: 'pointer', flexShrink: 0, marginLeft: narrow ? 0 : 16, minHeight: 44 }}>
            <Avatar size="small" icon={<UserOutlined />} />
            {!narrow && <span>{getUserDisplayName(session)}</span>}
          </Space>
        </Dropdown>
      </Header>
      <AntLayout>
        {!narrow && hasSideMenu && (
          <Sider
            width={SIDER_WIDTH}
            collapsedWidth={0}
            collapsed={siderCollapsed}
            collapsible
            trigger={null}
            style={{ background: '#fff', overflow: 'hidden' }}
          >
            <Menu
              mode="inline"
              selectedKeys={[pathname]}
              items={sideItems}
              onClick={({ key }) => navigate(key)}
              style={{ height: '100%', borderRight: 0 }}
            />
          </Sider>
        )}
        <Content style={{ padding: narrow ? 12 : 24, background: '#f0f2f5', minWidth: 0 }}>
          <Outlet />
        </Content>
      </AntLayout>
      <Drawer
        title="菜单"
        placement="left"
        width={280}
        open={narrow && navOpen}
        onClose={() => setNavOpen(false)}
        styles={{ body: { padding: 0 } }}
      >
        {hasSideMenu && (
          <>
            <div style={{ padding: '12px 16px', fontWeight: 600 }}>{currentConfig?.label}</div>
            <Menu
              mode="inline"
              selectedKeys={[pathname]}
              items={sideItems}
              onClick={({ key }) => go(String(key))}
              style={{ borderRight: 0 }}
            />
          </>
        )}
        <div style={{ padding: '12px 16px', color: '#888' }}>其它模块</div>
        {visibleMenus
          .filter((item) => item.key !== currentTopKey)
          .map((item) => (
            <Button
              key={item.key}
              type="text"
              block
              icon={item.icon}
              onClick={() => go(firstNavigableChild(item) || item.key)}
              style={{ textAlign: 'left', height: 44, paddingInline: 16 }}
            >
              {item.label}
            </Button>
          ))}
      </Drawer>
    </AntLayout>
  );
};

export default Layout;
